import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { request, formatDate, patientname } from '../../../services/utilities';
import { notifyError } from '../../../services/notify';
import TableLoading from '../../TableLoading';
import MonitoringSheetDetailsForm from '../MonitoringSheetDetailsForm';

const dash = value => (value === undefined || value === null || value === '' ? '--' : value);

const FullStimulationSheet = ({ patient, ivf }) => {
	const [loading, setLoading] = useState(true);
	const [enrollment, setEnrollment] = useState(null);
	const [stimulation, setStimulation] = useState(null);
	const [husband, setHusband] = useState(null);
	const [wife, setWife] = useState(null);
	const [opu, setOpu] = useState(null);
	const [or, setOr] = useState(null);
	const [embryoTransfer, setEmbryoTransfer] = useState(null);
	const [freezing, setFreezing] = useState(null);
	const [showMonitoringForm, setShowMonitoringForm] = useState(false);

	const fetchSummary = async () => {
		try {
			setLoading(true);
			const url = `ivf/summary/${ivf.id}`;
			const rs = await request(url, 'GET', true);
			if (rs.success) {
				const { result } = rs;
				const { stimulation, embryo_transfer, husband, or, opu, wife, freezing } = result;
				setEnrollment(result);
				setStimulation(stimulation);
				setHusband(husband);
				setWife(wife);
				setOpu(opu);
				setOr(or);
				setFreezing(freezing);
				setEmbryoTransfer(Array.isArray(embryo_transfer) ? embryo_transfer[0] : embryo_transfer);
			}
			setLoading(false);
		} catch (error) {
			console.log(error);
			setLoading(false);
			notifyError('error fetching monitoring sheet data');
		}
	};

	useEffect(() => {
		fetchSummary();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const openMonitoringForm = () => {
		document.body.classList.add('modal-open');
		setShowMonitoringForm(true);
	};

	const closeMonitoringForm = () => {
		document.body.classList.remove('modal-open');
		setShowMonitoringForm(false);
	};

	const onMonitoringSheetSaved = data => {
		setEnrollment(prev => ({ ...prev, monitoringSheetDetails: data }));
	};

	if (loading) {
		return <TableLoading />;
	}

	// dedupe day rows, keeping the most recently saved entry per day, sorted numerically
	const dayRows = (() => {
		const byDay = {};
		(stimulation?.data || []).forEach(row => {
			const existing = byDay[row.day];
			if (!existing || moment(row.createdAt).isAfter(moment(existing.createdAt))) {
				byDay[row.day] = row;
			}
		});
		return Object.values(byDay).sort((a, b) => parseInt(a.day, 10) - parseInt(b.day, 10));
	})();

	const monitoringDetails = enrollment?.monitoringSheetDetails || {};
	const baselineScans = monitoringDetails.baselineScans || [];
	const semen = monitoringDetails.semen || {};
	const medications = monitoringDetails.medications || {};
	const consent = monitoringDetails.consent || {};

	const follicleSummary = follicles =>
		Array.isArray(follicles) && follicles.length
			? follicles.map((f, i) => <div key={i}>{`${dash(f.totalFollicles)} @ ${dash(f.measurement)}mm`}</div>)
			: '--';

	return (
		<div className="element-wrapper">
			<div className="container-fluid">
				{/* Header */}
				<div className="container mt-3">
					<h6>Patient Details</h6>
					<div className="table-responsive">
						<table className="table table-padded">
							<tbody>
								<tr>
									<td>Patient Name</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{patientname(wife || patient)}</td>
									<td>Phone</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash((wife || patient)?.phone_number)}</td>
									<td>DOB</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{formatDate((wife || patient)?.date_of_birth)}</td>
								</tr>
								<tr>
									<td>Partner Name</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{husband ? patientname(husband) : '--'}</td>
									<td>Partner Phone</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(husband?.phone_number)}</td>
									<td>Partner DOB</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{husband ? formatDate(husband?.date_of_birth) : '--'}</td>
								</tr>
								<tr>
									<td>Indication</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(enrollment?.indication)}</td>
									<td>Treatment Plan</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(enrollment?.treatmentPlan)}</td>
									<td>Commencement Date</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{formatDate(enrollment?.dateOfCommencement)}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				{/* Baseline scan / semen / medication / consent */}
				<div className="container mt-3">
					<div className="d-flex justify-content-between align-items-center">
						<h6>Baseline Scan, Semen, Medication & Consent</h6>
						<a className="btn btn-sm btn-secondary text-white" onClick={openMonitoringForm}>
							Edit
						</a>
					</div>
					<div className="table-responsive">
						<table className="table table-padded">
							<thead>
								<tr>
									<th>Scan</th>
									<th>Date</th>
									<th>Lining</th>
									<th>RT</th>
									<th>LT</th>
								</tr>
							</thead>
							<tbody>
								{[0, 1].map(i => (
									<tr key={i}>
										<td>Scan {i + 1}</td>
										<td>{formatDate(baselineScans[i]?.date)}</td>
										<td>{dash(baselineScans[i]?.lining)}</td>
										<td>{dash(baselineScans[i]?.right)}</td>
										<td>{dash(baselineScans[i]?.left)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className="table-responsive">
						<table className="table table-padded">
							<tbody>
								<tr>
									<td>Semen S/A Date</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{formatDate(semen.saDate)}</td>
									<td>DS</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(semen.ds)}</td>
									<td>Code</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(semen.code)}</td>
								</tr>
								<tr>
									<td>Ordered/Stored</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(semen.orderedStored)}</td>
									<td>MESA/PESA/TESA</td>
									<td style={{ backgroundColor: '#f3f3f3' }} colSpan={3}>
										{dash(semen.mesaPesaTesa)}
									</td>
								</tr>
								<tr>
									<td>Medication</td>
									<td style={{ backgroundColor: '#f3f3f3' }} colSpan={5}>
										{[
											medications.aspirin && 'Aspirin',
											medications.dxmPrednisolone && 'DXM/Prednisolone',
											medications.metformin && 'Metformin',
											medications.cabergolin && 'Cabergolin',
										]
											.filter(Boolean)
											.join(', ') || '--'}
									</td>
								</tr>
								<tr>
									<td>Consent Given</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{consent.givenToPatient ? `Yes (${formatDate(consent.givenDate)})` : 'No'}</td>
									<td>Consent Returned</td>
									<td style={{ backgroundColor: '#f3f3f3' }} colSpan={3}>
										{consent.returnedByPatient ? `Yes (${formatDate(consent.returnedDate)})` : 'No'}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				{/* Protocol values (once per cycle) */}
				<div className="container mt-3">
					<h6>Protocol</h6>
					<div className="table-responsive">
						<table className="table table-padded">
							<tbody>
								<tr>
									<td>LMP</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{formatDate(stimulation?.lmp)}</td>
									<td>LP/SP/Antagonist</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(stimulation?.antagonist)}</td>
									<td>Pill</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(stimulation?.piii)}</td>
									<td>Suprecur (Buserelin)/Zoladex</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(stimulation?.suprecur)}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				{/* Day-by-day monitoring grid */}
				<div className="container mt-3">
					<h6>Stimulation Monitoring</h6>
					{dayRows.length === 0 ? (
						<p>No stimulation monitoring entries recorded yet.</p>
					) : (
						<div className="table-responsive">
							<table className="table table-bordered table-sm">
								<thead>
									<tr>
										<th>Cy/St Day</th>
										{dayRows.map(row => (
											<th key={row.id || row.day}>{row.day}</th>
										))}
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>&lt;10mm Endo</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{dash(row.endo)}</td>
										))}
									</tr>
									<tr>
										<td>FSH</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{dash(row.fsh)}</td>
										))}
									</tr>
									<tr>
										<td>HMG</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{dash(row.hmg)}</td>
										))}
									</tr>
									<tr>
										<td>GnRHa</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{dash(row.gnrh)}</td>
										))}
									</tr>
									<tr>
										<td>Antagonist</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{dash(row.ant)}</td>
										))}
									</tr>
									<tr>
										<td>E2</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{dash(row.e_two)}</td>
										))}
									</tr>
									<tr>
										<td>Cyclogest</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{dash(row.cyclogest)}</td>
										))}
									</tr>
									<tr>
										<td>Others</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{dash(row.others)}</td>
										))}
									</tr>
									<tr>
										<td>Right Ovary (follicles @ mm)</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{follicleSummary(row.right)}</td>
										))}
									</tr>
									<tr>
										<td>Left Ovary (follicles @ mm)</td>
										{dayRows.map(row => (
											<td key={row.id || row.day}>{follicleSummary(row.left)}</td>
										))}
									</tr>
								</tbody>
							</table>
						</div>
					)}
				</div>

				{/* Milestones, pulled from other IVF tabs */}
				<div className="container mt-3 mb-4">
					<h6>Milestones</h6>
					<div className="table-responsive">
						<table className="table table-padded">
							<tbody>
								<tr>
									<td>OPU Date</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{formatDate(opu?.procedureTime)}</td>
									<td>Oocytes Retrieved (OPU)</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(opu?.oocytesRetrieved)}</td>
									<td>OR Count</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(or?.num_of_oocytes)}</td>
								</tr>
								<tr>
									<td>Embryo Transfer Date</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{formatDate(embryoTransfer?.createdAt)}</td>
									<td>E.SET (Embryos Transferred)</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(embryoTransfer?.embryoTransfered)}</td>
									<td>Day</td>
									<td style={{ backgroundColor: '#f3f3f3' }}>{dash(embryoTransfer?.day)}</td>
								</tr>
								<tr>
									<td>Freezing (Sperm) Date</td>
									<td style={{ backgroundColor: '#f3f3f3' }} colSpan={5}>
										{formatDate(freezing?.sperm?.date)}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{showMonitoringForm && (
				<MonitoringSheetDetailsForm closeModal={closeMonitoringForm} ivf={enrollment} onSaved={onMonitoringSheetSaved} />
			)}
		</div>
	);
};

export default FullStimulationSheet;
